import React, { useMemo, useState } from 'react';
import { t } from 'i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DataTable,
  useToast,
  Label,
} from '@cargomatic/ui';
import { useRateConditions } from 'hooks/useLaneRate';
import { ArrowDownIcon, ArrowUpIcon, LoaderCircle } from 'lucide-react';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { updateRateSequence } from 'utils/AccessorialRequestWebAPIUtils';
import { getColumns } from './rate-condition-columns';
import { CustomerTypeBadge } from './CustomerTypeBadge';
import moment from 'moment';

const highlighted = {
  rateCustomer: true,
  rateMarket: true,
  rateEquipmentType: true,
  rateEquipmentSize: true,
  rateHazardous: true,
  rateOrderType: true,
  rateSteamshipOrRailroad: true,
};

export function EditRateSequenceModal({
  laneRate,
  open = false,
  onOpenChange,
  onSaveSuccess,
}) {
  const { laneId, customerId } = laneRate;
  const { toast } = useToast();
  const { data, isLoading } = useRateConditions({ customerId, laneId });
  const [isSaving, setIsSaving] = useState(false);
  const [rateSequenceList, setRateSequenceList] = useState([]);
  const columns = getColumns();

  const sequencedRates = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    if (rateSequenceList.length > 0) {
      return rateSequenceList;
    }
    return data;
  }, [data, rateSequenceList]);

  const lastIndex = useMemo(() => {
    if (isLoading || !data || data.length === 0) {
      return 0;
    }
    return data.length - 1;
  }, [data, isLoading]);

  function onSequenceRate({ index, dir }) {
    const newIndex = dir === 'up' ? index - 1 : index + 1;

    const currentItem = { ...sequencedRates[index] };
    const itemToSwap = { ...sequencedRates[newIndex] };

    const resequencedList = sequencedRates.map((row, rowIndex) => {
      if (rowIndex === index) {
        return itemToSwap;
      }
      if (rowIndex === newIndex) {
        return currentItem;
      }
      return row;
    });
    setRateSequenceList(resequencedList);
  }

  const handleUpdateRateSequence = async (laneId, customer, sequencedRates) => {
    const updatedSequenceDates = sequencedRates.map((laneRate, index) => {
      return {
        laneRateId: laneRate.laneRateId,
        sequenceDate: moment().add(index, 'seconds'),
      };
    });

    await updateRateSequence({
      laneId,
      customer,
      laneRates: updatedSequenceDates,
    });
  };

  const mappedColumnDefs = useMemo(() => {
    return [
      {
        id: 'sequence',
        header: 'Sequence',
        enableSorting: false,
        enableHiding: false,
        cell: (props) => {
          const { row } = props;
          const { index } = row;
          const sequence = index + 1;

          return (
            <div className="flex items-center space-x-2">
              <span>{sequence}.</span>
              <Button
                variant="outline"
                className="ml-2 w-8 h-8"
                size="icon"
                disabled={index === 0}
                onClick={() => onSequenceRate({ index, dir: 'up' })}
              >
                <ArrowUpIcon className="w-4 h-4 flex-shrink-0" />
              </Button>
              <Button
                variant="outline"
                className="w-8 h-8"
                size="icon"
                disabled={index === lastIndex}
                onClick={() => onSequenceRate({ index, dir: 'down' })}
              >
                <ArrowDownIcon className="w-4 h-4 flex-shrink-0" />
              </Button>
            </div>
          );
        },
      },
      ...columns,
    ];
  }, [sequencedRates]);

  const options = {
    columns: mappedColumnDefs,
    data: sequencedRates,
    enableRowSelection: false,
    getCoreRowModel: getCoreRowModel(),
  };
  const table = useReactTable(options);

  async function handleSaveRateSequences() {
    try {
      const { laneId, customerId: customer } = laneRate;

      setIsSaving(true);
      await handleUpdateRateSequence(laneId, customer, sequencedRates);
      toast({ description: 'Rate sequence updated!' });
      onSaveSuccess();
    } catch (e) {
      toast({ variant: 'destructive', description: e.message });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="min-w-full">
        <DialogHeader>
          <DialogTitle>{t('EDIT_RATE_SEQUENCE')}</DialogTitle>
          <DialogDescription>
            <div className="mb-4">{t('EDIT_RATE_SEQUENCE_DESCRIPTION')}</div>
            <div className="flex space-x-16 items-center flex-wrap">
              <div className="flex space-x-4 items-center">
                <Label>{t('CUSTOMER_TYPE')}</Label>
                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                  <CustomerTypeBadge customerType={laneRate?.customerType} />
                </dd>
              </div>
              <div className="flex space-x-4 items-center">
                <Label>{t('CUSTOMER')}</Label>
                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                  {laneRate?.customerName || 'Sabe Trucking'}
                </dd>
              </div>
              <div className="flex space-x-4 items-center">
                <Label>{t('ORIGIN')}</Label>
                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                  {laneRate?.origin || 'Port of LA/LB'}
                </dd>
              </div>
              <div className="flex space-x-4 items-center">
                <Label>{t('DESTINATION')}</Label>
                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-0">
                  {laneRate?.destination || '91422'}
                </dd>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto mb-4" style={{ maxHeight: '60vh' }}>
          <DataTable
            table={table}
            isLoading={isLoading}
            highlighted={highlighted}
            skeletonRowCount={5}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onOpenChange} disabled={isSaving}>
            {t('CANCEL')}
          </Button>
          <Button
            type="submit"
            onClick={handleSaveRateSequences}
            disabled={isSaving}
          >
            <span>{t('SAVE_CHANGES')}</span>
            {isSaving && <LoaderCircle className="w-4 h-4 animate-spin ml-2" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
